// packages/web-ui/src/components/composer/Composer.tsx
//
// Story W2-S7 — `<Composer>` React component. Owns the textarea, chip
// strip, tool bar, drag-and-drop file zone, slash-menu trigger,
// mention-popup, and ambiguous-mention guard.
//
// Wiring contract (props):
//   - `sessionId`: passes through to dispatch context (chat slice owns the
//     actual session state — composer never reads/writes it directly).
//   - `onSend(payload)`: parent dispatches the chat-slice `sendMessage`.
//   - `onFileDropped(file)`: parent routes to the W2-S6 `<FileUploadConfirm>`
//     card.
//   - `mentionFetcher`: async resolver for `useMentionAutocomplete`.
//   - `onCreateSession`: dispatched by `/new`.
//   - `onClearMessages`: dispatched by `/clear`.
//   - `onOpenHelp`: dispatched by `/help`.
//   - `onCancelTurn`: dispatched by `/cancel`.
//   - `onRegenerateLast`: dispatched by `/regenerate`.
//   - `onRecallLastUserMessage`: returns the last user-message body for the
//     `↑` (recall) keyboard shortcut. May return null when no history.
//   - `onMentionRecorded`: optional — called when the user picks a
//     mention; lets the parent record recency for `useMentionAutocomplete`.
//   - `disabled`: forwarded by the chat panel during regenerate-last.
//
// Layout per AC #1 / UX §6.1.6:
//   Row 1 — textarea (auto-grow 40 → 200 px)
//   Row 2 — ChipStrip (24 px, hidden when chips.length === 0)
//   Row 3 — ToolBar (32 px)
//   Row 4 — InlineErrorBanner (only when ambiguous mention)

import type { JSX, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, AtSign, Paperclip, Send, Slash } from 'lucide-react';
import { clsx } from 'clsx';
import type { KbEntity } from '@tessa-lean/base/api-schemas/index.js';
import {
  useMentionAutocomplete,
  type MentionCandidateFetcher,
} from '../../hooks/useMentionAutocomplete.js';
import { useComposer, type ComposerSendPayload } from '../../hooks/useComposer.js';
import { useDropZone } from '../../hooks/useDropZone.js';
import { useSkillsForSlash } from '../../hooks/useSkillsForSlash.js';
import { useSlashCommands } from '../../hooks/useSlashCommands.js';
import { MentionPopup, handleMentionPopupKey } from '../mention-helper/index.js';
import { GlobalPalette, SlashMenu, type SlashDispatchContext } from '../slash-palette/index.js';
import { ChipStrip } from './ChipStrip.js';

export interface ComposerProps {
  sessionId: string;
  /** Send the composed message. Parent dispatches `chat.sendMessage` here. */
  onSend: (payload: ComposerSendPayload) => Promise<void> | void;
  /** Mention fetcher — async candidate resolver (kbApi.listEntities flavour). */
  mentionFetcher: MentionCandidateFetcher;
  /** File drop hand-off — parent routes to the W2-S6 upload card. */
  onFileDropped?: (file: File) => void;
  /** Optional: `↑` recall callback. Returns the last user-message body or null. */
  onRecallLastUserMessage?: () => string | null;
  /** Optional: emitted whenever a mention chip is inserted. */
  onMentionRecorded?: (candidate: { chipText: string; entityId: string; kind: string }) => void;
  /** Slash-command collaborators. */
  onCreateSession?: () => Promise<void> | void;
  onClearMessages?: () => Promise<void> | void;
  onOpenHelp?: () => void;
  onCancelTurn?: () => void;
  onRegenerateLast?: () => Promise<void> | void;
  /** Toast surface for command stubs. */
  dispatchToast?: (message: string, severity?: 'info' | 'warning' | 'error') => void;
  /** True while a regenerate is in flight. */
  disabled?: boolean;
  /** Provider availability — composer disables send when null. */
  providerId?: string | null;
  /**
   * Context-usage display — `tokensUsed` is the latest prompt-tokens
   * high-watermark for the active session (fed by the SSE
   * `chat:sessionUsage` event the orchestrator re-emits from
   * `agent:llmUsage`). Rendered as a percentage against `contextMax`
   * (default: 128k, the same budget `auto-compact` uses as its denominator).
   */
  tokensUsed?: number | null;
  contextMax?: number;
}

/**
 * Default context-window budget for the % display. Mirrors the value the
 * auto-compact subsystem uses (`packages/base/src/agent/auto-compact.ts`)
 * so the UI indicator and the auto-compact trigger share one number.
 */
const DEFAULT_CONTEXT_MAX = 128_000;

/**
 * Format `tokensUsed` against `contextMax` as the composer-footer context-
 * usage label. Exported (named) so the test suite can assert the exact
 * formatting without re-implementing it.
 */
export function formatContextUsage(
  tokensUsed: number | null,
  contextMax: number,
): string {
  const used = tokensUsed ?? 0;
  const pct = contextMax > 0 ? (used / contextMax) * 100 : 0;
  const pctStr = pct < 0.1 && used > 0 ? '<0.1' : pct.toFixed(1);
  return `ctx: ${pctStr}% (${formatK(used)} / ${formatK(contextMax)})`;
}

function formatK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n}`;
}

export function Composer(props: ComposerProps): JSX.Element {
  const {
    sessionId,
    onSend,
    mentionFetcher,
    onFileDropped,
    onRecallLastUserMessage,
    onMentionRecorded,
    onCreateSession,
    onClearMessages,
    onOpenHelp,
    onCancelTurn,
    onRegenerateLast,
    dispatchToast,
    disabled = false,
    providerId = 'default',
    tokensUsed = null,
    contextMax = DEFAULT_CONTEXT_MAX,
  } = props;

  const composer = useComposer();
  const {
    value,
    caret,
    setValue,
    setCaret,
    textareaRef,
    applyAutoGrow,
    chips,
    insertChip,
    removeChip,
    hasAmbiguousMention,
    isEmpty,
    buildSendPayload,
    clear,
  } = composer;

  // Slash menu + global palette wiring.
  const slash = useSlashCommands({ value, caret });
  const { commands: slashCommands } = useSkillsForSlash();

  // Mention autocomplete wiring.
  const mention = useMentionAutocomplete({
    value,
    caret,
    fetcher: mentionFetcher,
  });

  // Send-disabled rule (AC #1).
  const isSendDisabled = useMemo(() => {
    return isEmpty || hasAmbiguousMention || disabled || providerId === null;
  }, [isEmpty, hasAmbiguousMention, disabled, providerId]);

  // Dispatch context for slash commands.
  const slashCtx = useMemo<SlashDispatchContext>(
    () => ({
      sessionId,
      sendMessage: async (body: string) => {
        await onSend({ body, mentions: [], attachments: [] });
      },
      createSession: onCreateSession,
      clearMessages: onClearMessages,
      openHelp: onOpenHelp,
      cancelTurn: onCancelTurn,
      regenerateLast: onRegenerateLast,
      dispatchToast,
    }),
    [
      sessionId,
      onSend,
      onCreateSession,
      onClearMessages,
      onOpenHelp,
      onCancelTurn,
      onRegenerateLast,
      dispatchToast,
    ],
  );

  // Drop zone wiring.
  const { dragActive, handlers: dropHandlers } = useDropZone({
    onFileDropped: (file) => {
      onFileDropped?.(file);
    },
    enabled: onFileDropped !== undefined,
  });

  // Submit handler — runs on Enter (AC #3) AND form submit.
  // Clear synchronously *before* awaiting onSend so the input box empties
  // the moment the user clicks Send. The payload is captured by value, so
  // clearing local state can't affect the in-flight request.
  const submit = useCallback(async (): Promise<void> => {
    if (isSendDisabled) return;
    const payload = buildSendPayload();
    if (payload === null) return;
    clear();
    slash.setGlobalPaletteOpen(false);
    await onSend(payload);
  }, [buildSendPayload, clear, isSendDisabled, onSend, slash]);

  // Ctrl+Enter — submit + open new tab (AC #3).
  const submitAndNewTab = useCallback(async (): Promise<void> => {
    if (isSendDisabled) return;
    await submit();
    if (onCreateSession !== undefined) {
      await onCreateSession();
    }
  }, [isSendDisabled, submit, onCreateSession]);

  // Recall last user message (AC #3 ↑).
  const recallLast = useCallback((): boolean => {
    if (onRecallLastUserMessage === undefined) return false;
    if (value.trim().length > 0 || chips.length > 0) return false;
    const last = onRecallLastUserMessage();
    if (last === null) return false;
    setValue(last, last.length);
    return true;
  }, [onRecallLastUserMessage, value, chips.length, setValue]);

  // Textarea onChange handler (covers paste + drag-drop text + programmatic).
  const onTextareaInput = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
      setValue(event.target.value, event.target.selectionStart ?? event.target.value.length);
      applyAutoGrow(event.target);
    },
    [setValue, applyAutoGrow],
  );

  const onTextareaSelect = useCallback(
    (event: React.SyntheticEvent<HTMLTextAreaElement>): void => {
      const ta = event.currentTarget;
      setCaret(ta.selectionStart ?? ta.value.length);
    },
    [setCaret],
  );

  // Keyboard map (AC #3).
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>): void => {
      // Mention popup gets first dibs.
      if (mention.open) {
        const handled = handleMentionPopupKey({
          event,
          open: mention.open,
          candidates: mention.candidates,
          activeFilter: mention.activeFilter,
          activeIndex: mention.activeIndex,
          onSelect: (cand) => {
            const trigger = mention.trigger;
            if (trigger === null) return;
            insertChip(cand, trigger.atOffset, trigger.caretOffset - trigger.atOffset);
            onMentionRecorded?.({
              chipText: cand.chipText,
              entityId: cand.entityId,
              kind: cand.kind,
            });
          },
          onDismiss: () => {
            mention.dismiss();
          },
          onActiveIndexChange: mention.setActiveIndex,
          onFilterChange: mention.setActiveFilter,
        });
        if (handled) return;
      }

      // Enter family.
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          // Newline — let default through.
          return;
        }
        if (isSendDisabled) {
          // No-op: do not preventDefault, allow newline through (matches AC #3).
          return;
        }
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          void submitAndNewTab();
          return;
        }
        event.preventDefault();
        void submit();
        return;
      }

      if (event.key === 'Escape') {
        if (slash.globalPaletteOpen) {
          slash.setGlobalPaletteOpen(false);
          event.preventDefault();
          return;
        }
        if (slash.slashOpen) {
          // Slash menu close: clear the leading `/` so the menu disappears.
          if (value.startsWith('/')) {
            event.preventDefault();
            setValue('', 0);
            return;
          }
        }
        // Otherwise blur.
        textareaRef.current?.blur();
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowUp') {
        if (recallLast()) {
          event.preventDefault();
        }
        return;
      }
    },
    [
      mention,
      insertChip,
      onMentionRecorded,
      isSendDisabled,
      submit,
      submitAndNewTab,
      slash,
      value,
      setValue,
      textareaRef,
      recallLast,
    ],
  );

  // Auto-grow on first mount + whenever value changes from non-DOM events.
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta !== null) {
      applyAutoGrow(ta);
    }
  }, [value, applyAutoGrow, textareaRef]);

  // Re-focus the textarea when the composer transitions from disabled → enabled
  // (i.e. the streaming response just finished). Lets the user type the next
  // message without clicking back into the input. Guarded so we never steal
  // focus away from another element the user has tabbed/clicked into.
  const wasDisabled = useRef(disabled);
  useEffect(() => {
    if (wasDisabled.current && !disabled) {
      const ta = textareaRef.current;
      if (ta !== null && document.activeElement !== ta) {
        const active = document.activeElement;
        const userMovedFocus =
          active !== null &&
          active !== document.body &&
          (active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.tagName === 'BUTTON' ||
            active.tagName === 'SELECT' ||
            (active as HTMLElement).isContentEditable);
        if (!userMovedFocus) {
          ta.focus();
        }
      }
    }
    wasDisabled.current = disabled;
  }, [disabled, textareaRef]);

  const handleSlashMenuClose = useCallback((): void => {
    setValue('', 0);
  }, [setValue]);

  const handleAttachClick = useCallback((): void => {
    // The composer never opens the file dialog itself; W2-S6 owns that.
    dispatchToast?.('Use drag-and-drop or the upload card to attach files.', 'info');
  }, [dispatchToast]);

  const handleSlashIconClick = useCallback((): void => {
    if (value.length === 0) {
      setValue('/', 1);
      textareaRef.current?.focus();
    }
  }, [value, setValue, textareaRef]);

  const handleAtIconClick = useCallback((): void => {
    setValue(value + '@', value.length + 1);
    textareaRef.current?.focus();
  }, [value, setValue, textareaRef]);

  const handleSendClick = useCallback((): void => {
    void submit();
  }, [submit]);

  const tokensDisplay = formatContextUsage(tokensUsed, contextMax);

  return (
    <form
      role="form"
      aria-label="Compose chat message"
      data-testid="composer"
      data-drag-active={dragActive ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      {...dropHandlers}
      className={clsx(
        'relative flex flex-col gap-2 border-t border-soft bg-cream-100 p-3',
        dragActive && 'ring-2 ring-taupe-700 ring-offset-0',
      )}
    >
      {/* Row 1 — textarea */}
      <textarea
        ref={textareaRef}
        data-testid="composer-textarea"
        aria-label="Message"
        aria-multiline="true"
        aria-controls={mention.open ? 'tessa-mention-popup' : undefined}
        aria-expanded={mention.open}
        aria-activedescendant={
          mention.open && mention.candidates.length > 0
            ? `mention-popup-row-${mention.activeIndex}`
            : undefined
        }
        disabled={disabled}
        value={value}
        onChange={onTextareaInput}
        onSelect={onTextareaSelect}
        onKeyDown={onKeyDown}
        rows={1}
        style={{
          minHeight: '40px',
          maxHeight: '200px',
          transitionProperty: 'none',
        }}
        className="resize-none rounded border border-soft bg-cream-100 px-3 py-2 text-[13px] text-taupe-900 outline-none focus:ring-2 focus:ring-taupe-700"
      />

      {/* Mention popup (anchored above textarea by absolute positioning). */}
      {mention.open ? (
        <div
          data-testid="composer-mention-popup-anchor"
          className="absolute bottom-full left-3 z-40 mb-1"
        >
          <MentionPopup
            open={mention.open}
            candidates={mention.candidates}
            activeIndex={mention.activeIndex}
            activeFilter={mention.activeFilter}
            prefix={mention.prefix}
            onSelect={(cand) => {
              const trigger = mention.trigger;
              if (trigger === null) return;
              insertChip(cand, trigger.atOffset, trigger.caretOffset - trigger.atOffset);
              onMentionRecorded?.({
                chipText: cand.chipText,
                entityId: cand.entityId,
                kind: cand.kind,
              });
            }}
            onDismiss={mention.dismiss}
            onActiveIndexChange={mention.setActiveIndex}
            onFilterChange={mention.setActiveFilter}
          />
        </div>
      ) : null}

      {/* Slash menu (inline above textarea). */}
      <div className="relative">
        <SlashMenu
          open={slash.slashOpen}
          query={slash.slashQuery}
          ctx={slashCtx}
          onClose={handleSlashMenuClose}
          commands={slashCommands}
        />
      </div>

      {/* Row 2 — chip strip */}
      <ChipStrip chips={chips} onRemove={removeChip} />

      {/* Row 3 — tool bar */}
      <div
        data-testid="composer-toolbar"
        role="toolbar"
        aria-label="Composer tools"
        className="flex h-8 items-center gap-2"
      >
        <button
          type="button"
          aria-label="Attach file"
          data-testid="composer-attach"
          onClick={handleAttachClick}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-taupe-700 hover:bg-cream-200"
        >
          <Paperclip aria-hidden="true" className="h-4 w-4" />
        </button>
        {value.length === 0 ? (
          <button
            type="button"
            aria-label="Open slash menu"
            data-testid="composer-slash"
            onClick={handleSlashIconClick}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-taupe-700 hover:bg-cream-200"
          >
            <Slash aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Insert mention"
          data-testid="composer-mention-trigger"
          onClick={handleAtIconClick}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-taupe-700 hover:bg-cream-200"
        >
          <AtSign aria-hidden="true" className="h-4 w-4" />
        </button>
        <span
          data-testid="composer-token-count"
          className="ml-auto text-[11px] text-taupe-700"
        >
          {tokensDisplay}
        </span>
        <button
          type="submit"
          aria-label="Send message"
          aria-disabled={isSendDisabled}
          disabled={isSendDisabled}
          data-testid="composer-send"
          onClick={handleSendClick}
          className={clsx(
            'inline-flex h-7 items-center gap-1 rounded px-3 text-[12px] font-medium',
            isSendDisabled
              ? 'cursor-not-allowed bg-cream-300 text-taupe-700'
              : 'bg-taupe-700 text-cream-100 hover:bg-taupe-900',
          )}
        >
          <Send aria-hidden="true" className="h-3 w-3" />
          Send
        </button>
      </div>

      {/* Row 4 — ambiguous-mention banner */}
      {hasAmbiguousMention ? (
        <div
          data-testid="composer-ambiguous-banner"
          role="status"
          aria-live="polite"
          className="border-accent-amber bg-cream-50 text-fg flex h-6 items-center gap-2 rounded-none border-l-2 px-2 text-[12px]"
        >
          <AlertTriangle aria-hidden="true" className="h-3 w-3" />
          <span>Mention is ambiguous. Refine and resend.</span>
        </div>
      ) : null}

      {/* Global palette modal (rendered out of the form on Ctrl+P). */}
      <GlobalPalette
        open={slash.globalPaletteOpen}
        ctx={slashCtx}
        onClose={() => {
          slash.setGlobalPaletteOpen(false);
        }}
        commands={slashCommands}
      />
    </form>
  );
}

/** Default no-op fetcher used by tests / placeholder embeds. */
export const noopMentionFetcher: MentionCandidateFetcher = async () => [];

/** Re-export type so consumers don't double-import. */
export type { ComposerSendPayload, KbEntity };
